import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Trash2 } from "lucide-react";
import { JourneyShell } from "@/components/journey/JourneyShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/auth/AuthContext";
import { useJourneyContent } from "@/hooks/useJourneyContent";
import { deleteMyData, getAccount, reportDownloadUrl } from "@/lib/journeyApi";
import "@/pages/Journey.css";

export default function Account() {
  const { ui, loading } = useJourneyContent();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    let active = true;
    getAccount().then((data) => { if (active) setAccount(data); });
    return () => { active = false; };
  }, []);

  if (loading || !ui || !account) return <div className="journey-loading" data-testid="account-loading-state" />;
  const copy = ui.account;

  const exit = async () => {
    await signOut();
    navigate("/", { replace: true });
  };
  const remove = async () => {
    await deleteMyData(confirmation);
    await exit();
  };

  return (
    <JourneyShell>
      <section className="journey-panel journey-panel--wide" data-testid="account-panel">
        <div className="journey-heading-row">
          <h1 data-testid="account-title">{copy.title}</h1>
          <Button variant="ghost" size="icon" onClick={exit} aria-label={copy.sign_out} data-testid="account-signout-button"><LogOut /></Button>
        </div>
        {account.reports.length === 0 ? (
          <p data-testid="account-empty-state">{copy.empty}</p>
        ) : (
          <div className="journey-history" data-testid="account-report-list">
            {account.reports.map((report) => (
              <div key={report.report_id} data-testid={`account-report-${report.report_id}`}>
                <div><strong>{report.kind === "free" ? copy.free_row : copy.paid_row}</strong><span>{report.file_name}</span></div>
                <a href={reportDownloadUrl(report.report_id)} aria-label={copy.download} data-testid={`account-report-download-${report.report_id}`}><Download /></a>
              </div>
            ))}
          </div>
        )}
        {account.orders.map((order) => (
          <div className="journey-order" key={order.order_id} data-testid={`account-order-${order.order_id}`}>
            <span>{order.ref}</span>
            <strong>{order.state === "paid" ? ui.unlock.paid_badge : ui.unlock.pending_badge}</strong>
          </div>
        ))}
        <Button className="journey-primary" onClick={() => navigate("/check?new=1")} data-testid="account-new-check-button">{copy.new_check}</Button>
        <div className="journey-divider" />
        <h2>{copy.delete_title}</h2>
        <p>{copy.delete_body}</p>
        <div className="journey-delete">
          <Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder="DELETE" aria-label={copy.delete_confirm_label} data-testid="delete-confirmation-input" />
          <Button variant="destructive" onClick={remove} disabled={confirmation !== "DELETE"} data-testid="delete-account-button"><Trash2 /> {copy.delete_button}</Button>
        </div>
      </section>
    </JourneyShell>
  );
}